import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import facebook from "../Images/facebook.png";
import insta from "../Images/insta.png";
import linkdin from "../Images/linkdin.svg";
import { FaSearch, FaBuilding, FaSortAlphaDown } from 'react-icons/fa';

interface MemberItem {
  id: number;
  name: string;
  email: string;
  company_name?: string;
  designation?: string;
  profile?: string;
  role?: string;
  sub_role?: string;
  about_company?: string;
  point?: number;
  social?: string;
  number?: string;
  status?: string;
  delete?: number;
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const Members = () => {
  const [membersList, setMembersList] = useState<MemberItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeLetter, setActiveLetter] = useState<string | null>(null);

  useEffect(() => {
    const fetchMembers = async () => {
      setLoading(true);
      try {
        const response = await axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo');
        const list: MemberItem[] = Array.isArray(response.data) ? response.data : [];

        // Active members only — exclude deleted, super-admin, and inactive statuses
        const activeMembers = list
          .filter((m: any) =>
            m.delete !== 1 &&
            m.role !== 'super-admin' &&
            m.status !== 'Inactive' &&
            m.status !== 'Deleted'
          )
          // Sort A→Z by name
          .sort((a, b) => a.name.trim().localeCompare(b.name.trim(), 'en', { sensitivity: 'base' }));

        setMembersList(activeMembers);
      } catch {
        // Silent
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, []);

  // Derived filtered list
  const filteredMembers = useMemo(() => {
    let result = membersList;

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(m =>
        m.name.toLowerCase().includes(q) ||
        (m.company_name && m.company_name.toLowerCase().includes(q))
      );
    }

    if (activeLetter) {
      result = result.filter(m => m.name.trim().toUpperCase().startsWith(activeLetter));
    }

    return result;
  }, [searchTerm, activeLetter, membersList]);

  // Letters that actually have members
  const availableLetters = useMemo(
    () => new Set(membersList.map(m => m.name.trim().toUpperCase().charAt(0))),
    [membersList]
  );

  // Group members by first letter for section headers
  const grouped = useMemo(() => {
    const map: Record<string, MemberItem[]> = {};
    filteredMembers.forEach(m => {
      const letter = m.name.trim().toUpperCase().charAt(0);
      if (!map[letter]) map[letter] = [];
      map[letter].push(m);
    });
    return map;
  }, [filteredMembers]);

  const groupedKeys = Object.keys(grouped).sort();

  return (
    <div className="bg-[#101729] py-14 px-[5%] lg:px-[8%] min-h-screen text-slate-100">

      {/* ─── Section Header ─── */}
      <div className="mb-8 border-b border-slate-700/60 pb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div>
            <p className="text-[#FDBC12] text-xs font-black uppercase tracking-widest mb-1">Active Members · Recent Society Year</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
              Team <span className="text-[#FDBC12]">RMB Jaipur</span>
            </h2>
            <p className="text-slate-400 text-sm mt-1 flex items-center gap-2">
              <FaSortAlphaDown className="text-[#FDBC12]" />
              {membersList.length} members · Listed alphabetically
            </p>
          </div>

          {/* Search */}
          <div className="relative w-full md:w-72">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              placeholder="Search by name or company..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setActiveLetter(null); }}
              className="w-full pl-9 pr-4 py-2.5 bg-[#1E2B45] border border-slate-700 rounded-xl outline-none text-white text-sm font-medium focus:border-[#FDBC12] transition"
            />
          </div>
        </div>

        {/* ─── A–Z Quick Filter Bar ─── */}
        <div className="flex flex-wrap gap-1 mt-5">
          <button
            onClick={() => setActiveLetter(null)}
            className={`px-3 py-1 rounded-lg text-xs font-black transition ${
              activeLetter === null
                ? 'bg-[#FDBC12] text-[#0A2451]'
                : 'bg-slate-800 text-slate-400 hover:text-[#FDBC12]'
            }`}
          >
            ALL
          </button>
          {ALPHABET.map(letter => (
            <button
              key={letter}
              disabled={!availableLetters.has(letter)}
              onClick={() => setActiveLetter(activeLetter === letter ? null : letter)}
              className={`w-8 h-8 rounded-lg text-xs font-black transition ${
                activeLetter === letter
                  ? 'bg-[#FDBC12] text-[#0A2451]'
                  : availableLetters.has(letter)
                    ? 'bg-slate-800 text-slate-300 hover:bg-[#FDBC12]/20 hover:text-[#FDBC12]'
                    : 'bg-slate-900 text-slate-700 cursor-not-allowed'
              }`}
            >
              {letter}
            </button>
          ))}
        </div>
      </div>

      {/* ─── Loading Skeleton ─── */}
      {loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-[#1E2B45] rounded-xl p-5 flex gap-4 animate-pulse">
              <div className="w-20 h-20 rounded-full bg-slate-700 flex-shrink-0" />
              <div className="flex-1 space-y-3 py-1">
                <div className="h-4 bg-slate-700 rounded w-2/3" />
                <div className="h-3 bg-slate-800 rounded w-1/3" />
                <div className="h-3 bg-slate-800 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── Empty State ─── */}
      {!loading && filteredMembers.length === 0 && (
        <div className="text-center py-20 text-slate-500">
          <FaSearch size={36} className="mx-auto mb-4 opacity-30" />
          <p className="text-lg font-semibold">No members found matching "<span className="text-[#FDBC12]">{searchTerm}</span>"</p>
          <button onClick={() => { setSearchTerm(''); setActiveLetter(null); }} className="mt-4 text-xs text-[#FDBC12] underline">
            Clear filters
          </button>
        </div>
      )}

      {/* ─── Alphabetical Grouped Member Cards ─── */}
      {!loading && groupedKeys.map(letter => (
        <div key={letter} className="mb-8">
          {/* Letter Divider */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#FDBC12] flex items-center justify-center font-black text-[#0A2451] text-lg shadow-lg flex-shrink-0">
              {letter}
            </div>
            <div className="flex-1 h-px bg-slate-700/60" />
            <span className="text-slate-500 text-xs font-semibold">{grouped[letter].length} member{grouped[letter].length > 1 ? 's' : ''}</span>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {grouped[letter].map((m) => (
              <div
                key={m.id}
                className="bg-[#1E2B45] border border-slate-700/60 hover:border-[#FDBC12]/50 rounded-xl p-4 shadow-md flex gap-4 transition-all group"
              >
                {/* Profile Image */}
                <div className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full border-2 border-[#FDBC12] overflow-hidden flex-shrink-0 shadow-md group-hover:border-[#FDBC12] transition">
                  <img
                    src={
                      m.profile
                        ? (m.profile.startsWith('http') ? m.profile : `https://rmbbackend.rmbjaipur.com/${m.profile.replace(/\\/g, '/')}`)
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name)}&background=0A2451&color=FDBC12&bold=true`
                    }
                    alt={m.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name)}&background=0A2451&color=FDBC12&bold=true`;
                    }}
                  />
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="text-base font-bold text-white truncate">{m.name}</h3>
                        {m.company_name && (
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5 truncate">
                            <FaBuilding className="flex-shrink-0 text-[#FDBC12]" />
                            {m.company_name}
                          </p>
                        )}
                      </div>
                      <div className="bg-[#FDBC12]/15 border border-[#FDBC12]/30 text-[#FDBC12] text-[10px] font-black px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap">
                        🏆 {m.point || 0} Pts
                      </div>
                    </div>

                    {m.designation && (
                      <p className="text-[11px] text-slate-500 mt-1 line-clamp-1">{m.designation}</p>
                    )}
                  </div>

                  {/* Social & Connect */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700/50">
                    <div className="flex items-center gap-2">
                      <img className="w-4 h-4 cursor-pointer opacity-60 hover:opacity-100 transition" src={facebook} alt="Facebook" />
                      <img className="w-4 h-4 cursor-pointer opacity-60 hover:opacity-100 transition" src={insta} alt="Instagram" />
                      <img className="w-4 h-4 cursor-pointer opacity-60 hover:opacity-100 transition" src={linkdin} alt="LinkedIn" />
                    </div>
                    <button
                      onClick={() => alert(`Connect with ${m.name}\nEmail: ${m.email}\nPhone: ${m.number || 'N/A'}`)}
                      className="bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black px-3 py-1 rounded-lg text-[11px] shadow transition cursor-pointer"
                    >
                      Connect
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* ─── Footer Count ─── */}
      {!loading && filteredMembers.length > 0 && (
        <div className="text-center mt-8 text-slate-600 text-xs">
          Showing {filteredMembers.length} of {membersList.length} active members
        </div>
      )}
    </div>
  );
};

export default Members;